import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    const applyUser = (data) => {
      setUser(data);
      // Report this device's timezone on every app load (not just login) so
      // Today/Coming Up stay correct for sessions created before device_tz
      // tracking existed.
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz && tz !== data.device_tz) api.post("/auth/device-tz", { device_tz: tz }).catch(() => {});
      } catch { /* ignore */ }
    };
    try {
      const { data } = await api.get("/auth/me");
      applyUser(data);
    } catch (e) {
      // Expired 7-day access token? Exchange the 30-day refresh cookie for a
      // fresh access token before giving up — deep links, PWA restarts and
      // backgrounded tabs must not bounce a valid session to /login.
      if (e?.response?.status === 401) {
        try {
          const { data: r } = await api.post("/auth/refresh");
          if (r?.access_token) setToken(r.access_token);
          const { data } = await api.get("/auth/me");
          applyUser(data);
          return;
        } catch { /* truly signed out */ }
      }
      const status = e?.response?.status;
      if (status && status !== 401) {
        // Transient server error (5xx deploy blip, 429…) — the token is still
        // valid, so keep the session and retry quietly instead of logging out.
        setTimeout(() => refresh(), 4000);
        return;
      }
      if (!status && getToken()) {
        // Network blip/offline with a token on file — treat as offline, retry.
        setTimeout(() => refresh(), 4000);
        return;
      }
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from Google OAuth — AuthCallback exchanges the
    // session_id and establishes the session first; skip the /auth/me check.
    if (window.location.hash?.includes("session_id=")) { setReady(true); return; }
    if (getToken()) refresh();
    else { setUser(false); setReady(true); }
  }, [refresh]);

  const googleLogin = async (sessionId) => {
    try {
      const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
      const { data } = await api.post("/auth/google/session", { session_id: sessionId, device_tz: deviceTz });
      if (data.access_token) setToken(data.access_token);
      setUser(data);
      setReady(true);
      return { ok: true };
    } catch (e) {
      setReady(true);
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const login = async (email, password) => {
    try {
      const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
      const { data } = await api.post("/auth/login", { email, password, device_tz: deviceTz });
      if (data.access_token) setToken(data.access_token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
      const { data } = await api.post("/auth/register", { ...payload, device_tz: deviceTz });
      if (data.access_token) setToken(data.access_token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setToken(null);
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, googleLogin, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
